import React, { useRef, useEffect, useCallback } from 'react';
import {
  SignificationItem,
  SignificationResponse,
  TensionResponse,
  TriadResponse,
  StonesResponse,
} from '../../../../types/signification.types';
import {
  computeTriangleVertices,
  barycentricToScreen,
  barycentricColorBlend,
  screenToBarycentric,
  clampToSimplex,
  placementSummary,
  tensionPercentages,
  stonesSummary,
  type BarycentricCoord,
  type StonePlacementData,
} from '../../../../utils/signification.math';
import './SummaryScreen.css';

interface SummaryScreenProps {
  responses: SignificationResponse[];
  items: SignificationItem[];
  onDone: () => void;
}

export const SummaryScreen: React.FC<SummaryScreenProps> = ({ responses, items, onDone }) => {
  return (
    <div className="ss-container">
      <div className="ss-header">
        <div className="ss-title">Your responses</div>
        <div className="ss-subtitle">{responses.length} item{responses.length !== 1 ? 's' : ''} completed</div>
      </div>

      <div className="ss-items">
        {responses.map((response, i) => {
          const item = items.find(it => it.id === response.itemId) || items[i];
          if (!item) return null;
          return (
            <div key={response.itemId} className="ss-item">
              <div className="ss-item-prompt">{item.prompt}</div>
              {response.mode === 'tension' ? (
                <TensionSummaryRow response={response} item={item} />
              ) : response.mode === 'stones' ? (
                <StonesSummaryRow response={response as StonesResponse} item={item} />
              ) : (
                <TriadSummaryRow response={response as TriadResponse} item={item} />
              )}
            </div>
          );
        })}
      </div>

      <button className="ss-done-btn" onClick={onDone}>Done</button>
    </div>
  );
};

/* ── Tension summary row ── */
const TensionSummaryRow: React.FC<{ response: TensionResponse; item: SignificationItem }> = ({ response, item }) => {
  const pcts = tensionPercentages(response.weight);
  const leftColor = item.poles[0]?.color || '#C2AC8E';
  const rightColor = item.poles[1]?.color || '#8B9EB7';

  return (
    <div className="ss-tension">
      <div className="ss-tension-labels">
        <span style={{ color: leftColor }}>{item.poles[0]?.label}</span>
        <span style={{ color: rightColor }}>{item.poles[1]?.label}</span>
      </div>
      <div
        className="ss-tension-track"
        style={{ background: `linear-gradient(to right, ${leftColor}22, ${rightColor}22)` }}
      >
        <div
          className="ss-tension-dot"
          style={{
            left: `${response.weight * 100}%`,
            background: `color-mix(in srgb, ${leftColor} ${pcts[0]}%, ${rightColor})`,
          }}
        />
      </div>
      <div className="ss-pcts">
        <span>{pcts[0]}%</span>
        <span>{pcts[1]}%</span>
      </div>
    </div>
  );
};

/* ── Triad summary row ── */
const MINI_SIZE = 180;
const MINI_PADDING = 24;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

const TriadSummaryRow: React.FC<{ response: TriadResponse; item: SignificationItem }> = ({ response, item }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bary: BarycentricCoord = { a: response.weights[0], b: response.weights[1], c: response.weights[2] };
  const labels: [string, string, string] = [
    item.poles[0]?.label || 'A',
    item.poles[1]?.label || 'B',
    item.poles[2]?.label || 'C',
  ];
  const summary = placementSummary(bary, labels);

  const colorA = hexToRgb(item.poles[0]?.color || '#C2AC8E');
  const colorB = hexToRgb(item.poles[1]?.color || '#8B9EB7');
  const colorC = hexToRgb(item.poles[2]?.color || '#A8B4A0');

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = MINI_SIZE * dpr;
    canvas.height = MINI_SIZE * dpr;
    canvas.style.width = `${MINI_SIZE}px`;
    canvas.style.height = `${MINI_SIZE}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    const vertices = computeTriangleVertices(MINI_SIZE, MINI_SIZE, MINI_PADDING);

    // Triangle outline
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();

    // Faint color fill
    ctx.save();
    ctx.clip();
    const step = 6;
    for (let y = 0; y < MINI_SIZE; y += step) {
      for (let x = 0; x < MINI_SIZE; x += step) {
        const b = screenToBarycentric({ x, y }, vertices);
        if (b.a < -0.01 || b.b < -0.01 || b.c < -0.01) continue;
        const c = clampToSimplex(b);
        ctx.fillStyle = barycentricColorBlend(c, colorA, colorB, colorC);
        ctx.globalAlpha = 0.1;
        ctx.fillRect(x, y, step, step);
      }
    }
    ctx.restore();

    // Outline
    ctx.beginPath();
    ctx.moveTo(vertices.A.x, vertices.A.y);
    ctx.lineTo(vertices.B.x, vertices.B.y);
    ctx.lineTo(vertices.C.x, vertices.C.y);
    ctx.closePath();
    ctx.strokeStyle = 'rgba(26, 24, 20, 0.15)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Placement dot
    const screen = barycentricToScreen(bary, vertices);
    const color = barycentricColorBlend(bary, colorA, colorB, colorC);

    ctx.beginPath();
    ctx.arc(screen.x, screen.y, 6, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.globalAlpha = 1;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Vertex labels
    ctx.font = '600 10px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#1A1814';
    ctx.globalAlpha = 0.6;
    ctx.fillText(labels[0], vertices.A.x, vertices.A.y - 10);
    ctx.fillText(labels[1], vertices.B.x, vertices.B.y + 16);
    ctx.fillText(labels[2], vertices.C.x, vertices.C.y + 16);
  }, [bary, colorA, colorB, colorC, labels]);

  useEffect(() => { render(); }, [render]);

  return (
    <div className="ss-triad">
      <canvas ref={canvasRef} className="ss-triad-canvas" />
      <div className="ss-triad-summary">{summary}</div>
    </div>
  );
};

/* ── Stones summary row ── */
const STONE_COLORS = ['#C2AC8E', '#8B9EB7', '#A8B4A0', '#B8A9C9', '#C2889B'];

const StonesSummaryRow: React.FC<{ response: StonesResponse; item: SignificationItem }> = ({ response, item }) => {
  const placements: StonePlacementData[] = response.placements.map(p => ({
    stoneId: p.stoneId, x: p.x, y: p.y,
  }));

  const labels: Record<string, string> = {};
  for (const pole of item.poles) labels[pole.id] = pole.label;

  const summary = stonesSummary(placements, labels);

  return (
    <div className="ss-stones">
      <div className="ss-stones-canvas">
        <svg viewBox="0 0 200 200" width="200" height="200">
          {/* Proximity lines */}
          {placements.map((a, i) =>
            placements.slice(i + 1).map((b, j) => {
              const dist = Math.hypot(a.x - b.x, a.y - b.y);
              if (dist < 0.35) {
                return (
                  <line
                    key={`${i}-${i + j + 1}`}
                    x1={a.x * 180 + 10} y1={a.y * 180 + 10}
                    x2={b.x * 180 + 10} y2={b.y * 180 + 10}
                    stroke="rgba(26,24,20,0.1)"
                    strokeWidth="1"
                    strokeDasharray="3,3"
                  />
                );
              }
              return null;
            })
          )}
          {/* Stones */}
          {placements.map((p, i) => (
            <g key={p.stoneId}>
              <circle
                cx={p.x * 180 + 10}
                cy={p.y * 180 + 10}
                r="14"
                fill={STONE_COLORS[i % STONE_COLORS.length]}
                opacity="0.85"
              />
              <text
                x={p.x * 180 + 10}
                y={p.y * 180 + 10 + 4}
                textAnchor="middle"
                fontSize="8"
                fontWeight="700"
                fill="white"
              >
                {(labels[p.stoneId] || '').slice(0, 3).toUpperCase()}
              </text>
            </g>
          ))}
        </svg>
      </div>
      {summary && <div className="ss-stones-summary">{summary}</div>}
    </div>
  );
};
